import {isFunction, mapValues, toInteger, toString} from 'lodash';
import {get} from 'lodash/fp';

import {
  REMOTE_CONFIG_BANNER_TEXT,
  REMOTE_CONFIG_BUILD_NUMBER,
  REMOTE_CONFIG_CHAT_BETA_USER_EMAILS,
  REMOTE_CONFIG_CHAT_CODE_CONFIGS,
  REMOTE_CONFIG_CODE_CONFIGS,
  REMOTE_CONFIG_CSB_PLUS_BANNER,
  REMOTE_CONFIG_EDGE30_TEXT,
  REMOTE_CONFIG_FEATURE_FLAG,
  REMOTE_CONFIG_LAST_FORCED_BUILD_NUMBER,
  REMOTE_CONFIG_MAINTENANCE,
  REMOTE_CONFIG_MEAL_PLAN,
  REMOTE_CONFIG_OPTIONAL,
  REMOTE_CONFIG_UPDATE_NOTES,
  REMOTE_CONFIG_UPDATE_TYPE,
} from '/enum/remoteConfig.enum';
import {parse as parseJSON} from '/util/json.util';

import {FirebaseRemoteConfigBackend} from './remote-config-firebase';

// Set this to `true` to enable console logging of remote config events.
// Do NOT commit that change into the repo.
const LOG_REMOTE_CONFIG = false;

// Setting default values helps to ensure that
// both the local device & Remote servers are in sync
const CONFIG_DEFAULTS = {
  [REMOTE_CONFIG_BUILD_NUMBER]: 0,
  [REMOTE_CONFIG_LAST_FORCED_BUILD_NUMBER]: 0,
  [REMOTE_CONFIG_UPDATE_TYPE]: REMOTE_CONFIG_OPTIONAL,
  [REMOTE_CONFIG_UPDATE_NOTES]: [],
  [REMOTE_CONFIG_MAINTENANCE]: {},
  [REMOTE_CONFIG_FEATURE_FLAG]: {},
  [REMOTE_CONFIG_CODE_CONFIGS]: {},
  [REMOTE_CONFIG_CHAT_CODE_CONFIGS]: {},
  [REMOTE_CONFIG_CHAT_BETA_USER_EMAILS]: [],
  [REMOTE_CONFIG_BANNER_TEXT]: {},
  [REMOTE_CONFIG_EDGE30_TEXT]: {},
  [REMOTE_CONFIG_MEAL_PLAN]: {},
  [REMOTE_CONFIG_CSB_PLUS_BANNER]: {},
};

const CONFIG_FORMATTERS = {
  [REMOTE_CONFIG_BUILD_NUMBER]: toInteger,
  [REMOTE_CONFIG_LAST_FORCED_BUILD_NUMBER]: toInteger,
  [REMOTE_CONFIG_UPDATE_TYPE]: toString,
  [REMOTE_CONFIG_UPDATE_NOTES]: parseJSON,
  [REMOTE_CONFIG_MAINTENANCE]: parseJSON,
  [REMOTE_CONFIG_FEATURE_FLAG]: parseJSON,
  [REMOTE_CONFIG_CODE_CONFIGS]: parseJSON,
  [REMOTE_CONFIG_CHAT_CODE_CONFIGS]: parseJSON,
  [REMOTE_CONFIG_CHAT_BETA_USER_EMAILS]: parseJSON,
  [REMOTE_CONFIG_BANNER_TEXT]: parseJSON,
  [REMOTE_CONFIG_EDGE30_TEXT]: parseJSON,
  [REMOTE_CONFIG_MEAL_PLAN]: parseJSON,
  [REMOTE_CONFIG_CSB_PLUS_BANNER]: parseJSON,
};

export class RemoteConfigUtil {
  backend: any;

  defaults: any;

  formatters: any;

  constructor({backend, defaults = {}, formatters = {}}: any) {
    this.backend = backend;
    this.defaults = defaults;
    this.formatters = formatters;
  }

  /**
   * @returns {Promise}
   */
  setDefaults() {
    return this.backend.setDefaults(this.defaults);
  }

  /**
   * @returns {Promise}
   */
  getAll() {
    return this.backend.getAll().then((config: any) =>
      mapValues(config, (value: any, key: any) => {
        const formatValue: any = (this.formatters as any)[key];

        return isFunction(formatValue) ? formatValue(value) : value;
      }),
    );
  }

  /**
   * @param {String} key
   * @returns {Promise}
   */
  getValue(key: any) {
    return this.getAll().then(get(key));
  }
}

let RemoteConfigUtilDefaultInstance: any = null;

export const getRemoteConfigDefaultInstance = () => {
  if (RemoteConfigUtilDefaultInstance === null) {
    RemoteConfigUtilDefaultInstance = new RemoteConfigUtil({
      backend: new FirebaseRemoteConfigBackend({verbose: LOG_REMOTE_CONFIG}),
      defaults: CONFIG_DEFAULTS,
      formatters: CONFIG_FORMATTERS,
    });
  }

  return RemoteConfigUtilDefaultInstance;
};
